import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, Users, Star, Phone, ChevronRight, X, BadgeCheck, MessageCircle, Clock, Heart, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { getCities, getDistricts, getSettings, getTestimonials, getBlogs, getFeaturedVenues, createQuoteRequest, getPlatformStats } from '../services/api';
// import BulkQuoteForm from '../components/BulkQuoteForm'; // temporarily disabled
import BlogCard from '../components/BlogCard';
import FavoriteButton from '../components/FavoriteButton';
import AuthModal from '../components/AuthModal';
import LazyImage from '../components/LazyImage';
import RecentlyViewed from '../components/RecentlyViewed';
import SmartSearchBar from '../components/SmartSearchBar';
import { VenueSkeletonGrid } from '../components/VenueSkeleton';
import QuoteWizard from '../components/QuoteWizard';
import VenueImage from '../components/VenueImage';
import NisanHesaplayiciBanner from '../components/NisanHesaplayiciBanner';
import LiveActivityFeed from '../components/LiveActivityFeed';
import { useToast } from '../hooks/use-toast';
import useDetectedCity from '../hooks/useDetectedCity';
import { locativeSuffix as trLocativeSuffix, locativeAdjectiveCity as trLocativeAdjectiveCity } from '../utils/turkishLocative';

// Cloudinary URL Optimizasyonu - Resim boyutunu ve kalitesini optimize eder
const optimizeImageUrl = (url, width = 400) => {
  if (!url || typeof url !== 'string') return '';
  
  // Cloudinary URL'lerini optimize et
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    const afterUpload = url.split('/upload/')[1];
    if (afterUpload && !afterUpload.startsWith('v')) {
      return url; // Zaten optimize edilmiş
    }
    return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto,fl_progressive/`);
  }
  
  return url;
};

// Quick Quote Modal Component for Featured Venues
const QuickQuoteModal = ({ venue, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  if (!isOpen || !venue) return null;

  const handleWizardSubmit = async (wizardData) => {
    setIsSubmitting(true);
    
    const eventTypeLabels = {
      'isteme-nisan': 'İsteme & Nişan',
      'kina': 'Kına',
      'dogum-gunu': 'Doğum Günü',
      'bebek-mevlidi': 'Bebek Mevlidi',
      'etkinlik': 'Etkinlik'
    };
    
    const cateringLabels = {
      'kendim': 'İkramlıkları kendim getireceğim',
      'ikramli': 'İkramlı İstiyorum'
    };
    
    const photographyLabels = {
      'yok': 'İstemiyorum',
      'fotograf': 'Fotoğraf',
      'hikaye': 'Hikaye',
      'fotograf-hikaye': 'Fotoğraf + Hikaye',
      'fotograf-video': 'Fotoğraf + Video',
      'video': 'Video'
    };
    
    const serviceLabels = {
      'garson': 'Garson Olsun',
      'kendimiz': 'Servisi kendimiz yaparız'
    };

    try {
      const formatMonth = (monthValue) => {
        const [month, year] = monthValue.split('-');
        const monthNames = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 
                            'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      };

      const notes = `Talep Detaylari
—
Organizasyon: ${eventTypeLabels[wizardData.eventType] || wizardData.eventType}
Kisi Sayisi: ${wizardData.guestCount}
Ikram: ${cateringLabels[wizardData.catering] || wizardData.catering}
Fotograf / Video: ${photographyLabels[wizardData.photography] || wizardData.photography}
Servis: ${serviceLabels[wizardData.service] || wizardData.service}
Planlanan Ay: ${formatMonth(wizardData.eventMonth)}`;

      const quoteData = {
        venueId: venue.id,
        venueName: venue.name,
        fullName: wizardData.fullName,
        phone: wizardData.phone,
        email: '',
        eventType: eventTypeLabels[wizardData.eventType] || wizardData.eventType,
        eventDate: wizardData.eventMonth,
        guestCount: parseInt(wizardData.guestCount.split('-')[0]) || 50,
        notes: notes
      };

      await createQuoteRequest(quoteData);
      
      toast({
        title: 'Talebiniz Alındı! 🎉',
        description: 'Mekan sahibi en kısa sürede size dönüş yapacaktır.',
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating quote request:', error);
      toast({
        title: 'Hata!',
        description: error.response?.data?.detail || 'Teklif talebi oluşturulamadı.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <h3 className="text-lg font-bold text-gray-900 pr-8">{venue.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-emerald-600">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Genellikle 1 saat içinde yanıt verir</span>
          </div>
        </div>
        
        {/* Wizard Form */}
        <div className="p-4">
          <QuoteWizard 
            venue={venue} 
            onSubmit={handleWizardSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const detectedCity = useDetectedCity();
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [featuredVenues, setFeaturedVenues] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState({ total_whatsapp_clicks: 0 });
  
  // Lazy load refs
  const blogsSectionRef = useRef(null);
  const testimonialsSectionRef = useRef(null);
  const [blogsLoaded, setBlogsLoaded] = useState(false);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Quick Quote Modal state for featured venues
  const [quoteModalVenue, setQuoteModalVenue] = useState(null);
  
  // Hero search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Autocomplete states
  const [cityInput, setCityInput] = useState('');
  const [districtInput, setDistrictInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const cityInputRef = useRef(null);
  const districtInputRef = useRef(null);

  // Typewriter animation states
  const cities_to_type = ['İstanbul', 'İzmir', 'Bursa', 'Ankara'];
  const [placeholderText, setPlaceholderText] = useState('');
  const [isSearchButtonPressed, setIsSearchButtonPressed] = useState(false);
  const [currentCityIndex, setCurrentCityIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadCriticalData();
    loadFeaturedVenues();
    loadSettings();
    loadPlatformStats();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
      setSelectedDistrict('');
    } else {
      setDistricts([]);
    }
  }, [selectedCity]);

  // Typewriter effect
  useEffect(() => {
    const currentCity = cities_to_type[currentCityIndex];
    
    const typewriterTimeout = setTimeout(() => {
      if (isTyping && currentCharIndex < currentCity.length) {
        // Typing
        setPlaceholderText(currentCity.substring(0, currentCharIndex + 1));
        setCurrentCharIndex(currentCharIndex + 1);
      } else if (isTyping && currentCharIndex === currentCity.length) {
        // Finished typing, wait then start deleting
        setTimeout(() => {
          setIsDeleting(true);
          setIsTyping(false);
        }, 1500); // Wait 1.5s before deleting
      } else if (isDeleting && currentCharIndex > 0) {
        // Deleting
        setPlaceholderText(currentCity.substring(0, currentCharIndex - 1));
        setCurrentCharIndex(currentCharIndex - 1);
      } else if (isDeleting && currentCharIndex === 0) {
        // Finished deleting, immediately move to next city and start typing
        setIsDeleting(false);
        setIsTyping(true);
        setCurrentCityIndex((currentCityIndex + 1) % cities_to_type.length);
      }
    }, isTyping ? 150 : 50); // Typing slower, deleting faster

    return () => clearTimeout(typewriterTimeout);
  }, [currentCharIndex, isTyping, isDeleting, currentCityIndex]);

  // Kritik veri: Şehirler (arama için)
  const loadCriticalData = async () => {
    try {
      const citiesRes = await getCities();
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    } finally {
      setCitiesLoading(false);
    }
  };

  // Öne çıkan mekanlar (bağımsız)
  const loadFeaturedVenues = async () => {
    try {
      const res = await getFeaturedVenues();
      setFeaturedVenues(res.data || []);
    } catch (error) {
      console.error('Error loading featured venues:', error);
    } finally {
      setFeaturedLoading(false);
    }
  };

  // Settings (bağımsız)
  const loadSettings = async () => {
    try {
      const res = await getSettings();
      setSettings(res.data);
    } catch (e) { /* ignore */ }
  };

  // Platform istatistikleri (bağımsız)
  const loadPlatformStats = async () => {
    try {
      const res = await getPlatformStats();
      setPlatformStats(res.data || { total_whatsapp_clicks: 0 });
    } catch (e) { /* ignore */ }
  };

  // Lazy: Bloglar (viewport'a girince yüklenir)
  const loadBlogsLazy = async () => {
    if (blogsLoaded) return;
    setBlogsLoaded(true);
    try {
      const res = await getBlogs(4);
      setBlogs(res.data || []);
    } catch (e) { console.error('Error loading blogs:', e); }
  };

  // Lazy: Testimonials (viewport'a girince yüklenir)
  const loadTestimonialsLazy = async () => {
    if (testimonialsLoaded) return;
    setTestimonialsLoaded(true);
    try {
      const res = await getTestimonials();
      setTestimonials(res.data || []);
    } catch (e) { console.error('Error loading testimonials:', e); }
  };

  // Lazy load observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === blogsSectionRef.current) loadBlogsLazy();
            if (entry.target === testimonialsSectionRef.current) loadTestimonialsLazy();
          }
        });
      },
      { rootMargin: '200px' } // 200px önceden yüklemeye başla
    );

    if (blogsSectionRef.current) observer.observe(blogsSectionRef.current);
    if (testimonialsSectionRef.current) observer.observe(testimonialsSectionRef.current);

    return () => observer.disconnect();
  }, [blogsLoaded, testimonialsLoaded]);

  const loadDistricts = async (cityId) => {
    try {
      const response = await getDistricts(cityId);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  // City autocomplete
  const handleCityInputChange = (value) => {
    setCityInput(value);
    setShowCitySuggestions(true);
    
    if (value.trim() === '') {
      setFilteredCities([]);
      setSelectedCity('');
      setSelectedDistrict('');
      setDistrictInput('');
      setDistricts([]);
    } else {
      const filtered = cities.filter(city => 
        city.name.toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'))
      );
      setFilteredCities(filtered);
    }
  };

  const handleCitySelect = async (city) => {
    setCityInput(city.name);
    setSelectedCity(city.id);
    setShowCitySuggestions(false);
    setDistrictInput('');
    setSelectedDistrict('');
    await loadDistricts(city.id);
  };

  const clearCity = () => {
    setCityInput('');
    setSelectedCity('');
    setSelectedDistrict('');
    setDistrictInput('');
    setDistricts([]);
    setShowCitySuggestions(false);
  };

  // District autocomplete
  const handleDistrictInputChange = (value) => {
    setDistrictInput(value);
    setShowDistrictSuggestions(true);
    
    if (value.trim() === '') {
      setFilteredDistricts([]);
      setSelectedDistrict('');
    } else {
      const filtered = districts.filter(district => 
        district.name.toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'))
      );
      setFilteredDistricts(filtered);
    }
  };

  const handleDistrictSelect = (district) => {
    setDistrictInput(district.name);
    setSelectedDistrict(district.id);
    setShowDistrictSuggestions(false);
  };

  const clearDistrict = () => {
    setDistrictInput('');
    setSelectedDistrict('');
    setShowDistrictSuggestions(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
      if (districtInputRef.current && !districtInputRef.current.contains(event.target)) {
        setShowDistrictSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    if (!selectedCity) {
      return; // İl seçilmeden arama yapılmasın
    }
    navigate(`/davet-evleri?cityId=${selectedCity}`);
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Helmet>
        <title>{settings?.siteName || 'DavetEviBul'} - {settings?.siteSlogan || 'Hayalinizdeki davet evini bulun'}</title>
        <meta 
          name="description" 
          content={settings?.metaDescription || "Davetevibul.com ile Türkiye genelindeki davet evlerini kolayca keşfedin. Söz, nişan, kız isteme, kına ve özel günler için en uygun mekanı bulun, teklif alın."} 
        />
        <meta 
          name="keywords" 
          content={settings?.metaKeywords || 'davet evi, nişan salonu, isteme salonu, organizasyon'} 
        />
        <link rel="canonical" href="https://davetevibul.com/" />
        {settings?.faviconUrl && <link rel="icon" href={settings.faviconUrl} />}
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davetevibul.com/" />
        <meta property="og:title" content={settings?.siteName ? `${settings.siteName} - ${settings.siteSlogan || 'Hayalinizdeki davet evini bulun'}` : 'DavetEviBul - Hayalinizdeki davet evini bulun'} />
        <meta property="og:description" content={settings?.metaDescription || "Türkiye genelindeki davet evlerini kolayca keşfedin. Söz, nişan, kız isteme, kına ve özel günler için en uygun mekanı bulun."} />
        <meta property="og:image" content="https://davetevibul.com/assets/logo/logo-horizontal.svg" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="og:site_name" content="DavetEviBul.com" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={settings?.siteName ? `${settings.siteName} - ${settings.siteSlogan || 'Hayalinizdeki davet evini bulun'}` : 'DavetEviBul - Hayalinizdeki davet evini bulun'} />
        <meta name="twitter:description" content={settings?.metaDescription || "Türkiye genelindeki davet evlerini kolayca keşfedin."} />
        
        {/* Organization + WebSite JSON-LD for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://davetevibul.com/#organization",
                "name": "davetevibul.com",
                "url": "https://davetevibul.com",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://davetevibul.com/assets/logo/logo-horizontal.svg",
                  "width": 320,
                  "height": 70
                },
                "description": "Türkiye'nin en büyük davet evi platformu. Söz, nişan, kız isteme, kına ve özel günler için en uygun mekanı bulun.",
                "foundingDate": "2024",
                "areaServed": {
                  "@type": "Country",
                  "name": "Türkiye"
                },
                "serviceType": ["Davet Evi Arama", "Mekan Kiralama", "Organizasyon Hizmetleri"],
                "sameAs": [
                  "https://www.instagram.com/davetevibulcom"
                ],
                "email": "info@davetevibul.com",
                "telephone": "+908503077197",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+908503077197",
                  "email": "info@davetevibul.com",
                  "contactType": "customer service",
                  "availableLanguage": "Turkish",
                  "areaServed": "TR"
                },
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "İstanbul",
                  "addressCountry": "TR"
                }
              },
              {
                "@type": "WebSite",
                "@id": "https://davetevibul.com/#website",
                "url": "https://davetevibul.com",
                "name": "davetevibul.com",
                "description": "Türkiye genelindeki davet evlerini kolayca keşfedin ve teklif alın.",
                "publisher": {
                  "@id": "https://davetevibul.com/#organization"
                },
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://davetevibul.com/davet-evleri?search={search_term_string}"
                  },
                  "query-input": "required name=search_term_string"
                },
                "inLanguage": "tr-TR"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section — dugun.com-style editorial (experimental, easy to revert) */}
      <div className="relative min-h-[80vh] md:min-h-[92vh] overflow-hidden bg-stone-900">
        {/* Background Image — real venue photography */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80')"
          }}
        >
          {/* Cinematic dark gradient for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/55 to-stone-900/85" aria-hidden="true" />
          {/* Warm rose glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, rgba(244,114,182,0.20) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(251,191,36,0.10) 0%, transparent 40%)'
            }}
            aria-hidden="true"
          />
          {/* Subtle film grain */}
          <div
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.6'/></svg>\")"
            }}
            aria-hidden="true"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 min-h-[80vh] md:min-h-[92vh] flex flex-col justify-center items-center py-16">
          {/* Tag */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 text-[10.5px] sm:text-[11.5px] tracking-[0.28em] font-semibold text-amber-200/95 uppercase">
              <span className="h-px w-8 bg-amber-200/70" aria-hidden="true" />
              Türkiye&apos;nin Davet Evi Rehberi
              <span className="h-px w-8 bg-amber-200/70" aria-hidden="true" />
            </span>
          </div>

          {/* Main Heading — editorial */}
          <div className="text-center mb-8 md:mb-10 max-w-3xl">
            <h1 className="font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
              Nişanınıza yakışan
              <span className="block italic font-normal bg-gradient-to-r from-amber-100 via-rose-100 to-amber-200 bg-clip-text text-transparent">
                mükemmel mekanı bulun
              </span>
            </h1>
            <p className="mt-5 text-white/85 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              {detectedCity.detected && detectedCity.city ? (
                <>
                  <span className="font-semibold text-amber-100">{detectedCity.city}</span>
                  {trLocativeSuffix(detectedCity.city)}ki davet evlerini keşfedin, ücretsiz teklif alın, doğrudan iletişime geçin.
                </>
              ) : (
                <>Şehrinizdeki davet evlerini keşfedin, ücretsiz teklif alın, doğrudan iletişime geçin.</>
              )}
            </p>
            {detectedCity.detected && detectedCity.slug && (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate(`/${detectedCity.slug}`)}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur ring-1 ring-white/25 hover:bg-white/15 hover:ring-white/40 px-4 py-1.5 text-[13px] font-semibold text-white shadow-sm transition-all"
                  data-testid="hero-city-shortcut"
                >
                  <MapPin className="h-3.5 w-3.5" strokeWidth={2.4} />
                  {trLocativeAdjectiveCity(detectedCity.city)} tüm davet evlerini gör
                  <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                </button>
              </div>
            )}
          </div>

          {/* Search Bar — kept intact per user request */}
          <div className="w-full max-w-xl px-2 relative z-[9999]">
            <SmartSearchBar minimal={true} />
          </div>

          {/* Category chips */}
          <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-2xl px-2">
            {['Nişan', 'Söz', 'Kına Gecesi', 'Doğum Günü', 'Kız İsteme'].map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => navigate(`/davet-evleri?type=${encodeURIComponent(label)}`)}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/8 backdrop-blur ring-1 ring-white/20 hover:bg-white/15 hover:ring-white/40 px-3.5 py-1.5 text-[12.5px] font-medium text-white/95 transition-all"
                data-testid={`hero-chip-${label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Bottom stat strip */}
          <div className="mt-10 pt-8 border-t border-white/12 flex flex-wrap items-center justify-center gap-8 sm:gap-14 text-white/85 text-[12.5px] sm:text-[13px]">
            <div className="flex items-center gap-2">
              <span className="font-display text-2xl sm:text-3xl font-semibold text-white">1.200+</span>
              <span className="uppercase tracking-widest text-[10.5px] text-white/60">Davet Evi</span>
            </div>
            <div className="hidden sm:block h-6 w-px bg-white/15" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <span className="font-display text-2xl sm:text-3xl font-semibold text-white">81</span>
              <span className="uppercase tracking-widest text-[10.5px] text-white/60">İl</span>
            </div>
            <div className="hidden sm:block h-6 w-px bg-white/15" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <span className="font-display text-2xl sm:text-3xl font-semibold text-amber-100">Ücretsiz</span>
              <span className="uppercase tracking-widest text-[10.5px] text-white/60">Teklif Al</span>
            </div>
          </div>
        </div>

        {/* Wave Transition to Next Section */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f9fafb"/>
          </svg>
        </div>
      </div>

      {/* Lead Capture: "Seni Arayalım" callback section */}
      <NisanHesaplayiciBanner />

      {/* Featured Venues Section */}
      {featuredLoading ? (
        <section className="py-12 bg-gray-50 relative z-0">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent inline-flex items-center gap-3 tracking-tight">
                Onaylı Davet Evleri
                <BadgeCheck className="w-7 h-7 sm:w-8 sm:h-8 text-blue-500 fill-blue-100" />
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-52 bg-gray-200 rounded-2xl" />
                  <div className="mt-3 h-4 bg-gray-200 rounded w-3/4" />
                  <div className="mt-2 h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : featuredVenues && featuredVenues.length > 0 && (
        <section className="py-12 bg-gray-50 relative z-0">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent inline-flex items-center gap-3 tracking-tight">
                Onaylı Davet Evleri
                <BadgeCheck className="w-7 h-7 sm:w-8 sm:h-8 text-blue-500 fill-blue-100" />
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredVenues.slice(0, 8).map((venue, venueIndex) => {
                const images = venue.images || [];
                const featuredIndex = venue.featuredImageIndex || 0;
                const mainImage = images[featuredIndex] || images[0] || '/placeholder-venue.jpg';
                const hasActiveCampaign = venue.activeCampaigns && venue.activeCampaigns.length > 0;
                const isPro = venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium';
                
                // Calculate minimum package price
                const getMinPrice = () => {
                  if (!venue.packages || venue.packages.length === 0) return null;
                  const pricesWithValue = venue.packages.filter(p => p.price > 0).map(p => p.price);
                  if (pricesWithValue.length === 0) return null;
                  return Math.min(...pricesWithValue);
                };
                const minPrice = getMinPrice();
                
                // Get campaign discount info
                const getCampaignDiscount = () => {
                  if (!hasActiveCampaign) return null;
                  const campaign = venue.activeCampaigns[0];
                  return {
                    name: campaign.campaignName,
                    discountType: campaign.discountType,
                    discountValue: campaign.discountValue
                  };
                };
                const campaignDiscount = getCampaignDiscount();
                
                return (
                  <div 
                    key={venue.id}
                    className={`relative ${hasActiveCampaign ? 'p-[3px] rounded-[26px] bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 animate-gradient-x' : ''}`}
                    data-testid={`featured-venue-${venue.id}`}
                  >
                    <div 
                      className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                      onClick={() => navigate(`/${venue.citySlug}/${venue.districtSlug}/${venue.slug}`)}
                    >
                      {/* Fotoğraf Alanı - Blur Background */}
                      <div className="relative aspect-[4/3] overflow-hidden bg-black">
                        {/* Sahipsiz mekan blur overlay */}
                        {!venue.isClaimed ? (
                          <VenueImage
                            src={mainImage}
                            alt={venue.name}
                            isClaimed={venue.isClaimed}
                            venueSlug={venue.slug}
                            className="w-full h-full"
                            imgClassName="group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <img 
                            src={optimizeImageUrl(mainImage, 500)}
                            alt={venue.name}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading={venueIndex < 4 ? "eager" : "lazy"}
                            decoding={venueIndex < 4 ? "sync" : "async"}
                          />
                        )}
                        
                        {/* Kampanya Badge - Sol üst */}
                        {hasActiveCampaign && campaignDiscount && (
                          <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse">
                            🔥 {campaignDiscount.discountType === 'percentage' 
                              ? `%${campaignDiscount.discountValue} İndirim!`
                              : `₺${campaignDiscount.discountValue?.toLocaleString('tr-TR')} İndirim!`}
                          </div>
                        )}
                        
                        {/* Fiyat Badge - Sol üst (kampanya yoksa) */}
                        {!hasActiveCampaign && minPrice && (
                          <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                            ₺{minPrice.toLocaleString('tr-TR')}&apos;den başlayan
                          </div>
                        )}
                        
                        {/* Paket sayısı badge */}
                        {!hasActiveCampaign && !minPrice && venue.packages && venue.packages.length > 0 && (
                          <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                            {venue.packages.length} Paket
                          </div>
                        )}

                        {/* Randevu Badge - Sağ alt köşe */}
                        {venue.visitSettings?.enabled && (
                          <div className="absolute bottom-3 right-3 z-10 bg-purple-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-[10px] font-semibold shadow-md flex items-center gap-1">
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Randevu
                          </div>
                        )}
                      </div>
                      
                      {/* İçerik Alanı */}
                      <div className="p-4 sm:p-5">
                        {/* Mekan Adı + Mavi Tik */}
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg text-gray-900 truncate">
                            {venue.name}
                          </h3>
                          {isPro && (
                            <BadgeCheck className="w-5 h-5 text-blue-500 fill-blue-100 flex-shrink-0" title="Doğrulanmış Mekan" />
                          )}
                        </div>
                        
                        {/* Rating */}
                        {(venue.googleRating || venue.rating) && (venue.googleRating > 0 || venue.rating > 0) && (
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            <span className="font-semibold text-gray-900 text-sm">{(venue.googleRating || venue.rating).toFixed(1)}</span>
                            {(venue.googleReviewCount || venue.reviewCount) > 0 && (
                              <span className="text-gray-500 text-sm">({venue.googleReviewCount || venue.reviewCount})</span>
                            )}
                          </div>
                        )}
                        
                        {/* Konum */}
                        <div className="flex items-center gap-1.5 text-gray-500 mb-3">
                          <MapPin className="w-4 h-4 text-pink-500 flex-shrink-0" />
                          <span className="text-sm truncate">{venue.districtName}, {venue.cityName}</span>
                        </div>
                        
                        {/* Kampanya ismi */}
                        {hasActiveCampaign && campaignDiscount && (
                          <div className="mb-3 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-lg truncate">
                            🎁 {campaignDiscount.name}
                          </div>
                        )}
                        
                        {/* Butonlar */}
                        <div className="flex flex-col gap-2">
                          {/* Akıllı Paket aktif mekanlar */}
                          {venue.smartPricingEnabled && (venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && (
                            <button 
                              className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white hover:from-rose-600 hover:via-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                              data-testid="smart-package-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/${venue.citySlug}/${venue.districtSlug}/${venue.slug}#smart-package-builder`);
                              }}
                            >
                              <Sparkles className="w-4 h-4" />
                              Hemen Paketini Olustur
                            </button>
                          )}
                          {/* Teklif Al - Smart pricing olmayan */}
                          {!venue.smartPricingEnabled && (venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && venue.quoteFormEnabled !== false && (
                            <button 
                              className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-md hover:shadow-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuoteModalVenue(venue);
                              }}
                            >
                              <MessageCircle className="w-4 h-4" />
                              Teklif Al
                            </button>
                          )}
                          
                          {/* Detayları Gör Butonu */}
                          <button 
                            className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1 ${
                              hasActiveCampaign 
                                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700' 
                                : 'border-2 border-gray-200 text-pink-600 hover:border-pink-300 hover:bg-pink-50'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/${venue.citySlug}/${venue.districtSlug}/${venue.slug}`);
                            }}
                          >
                            {hasActiveCampaign ? 'Kampanyayı Gör' : 'Detayları Gör'}
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View All Button */}
            <div className="text-center mt-6">
              <Button 
                onClick={() => navigate('/davet-evleri')}
                size="lg"
                className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white px-8"
              >
                Tüm Mekanları Gör
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Live Activity Feed — real-time social proof */}
      <LiveActivityFeed />

      {/* Cities Catalog Section */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent leading-[1.1] tracking-tight">
              Şehrinizde Aradığınız Davet Evi
              <span className="block text-gray-400 text-xl sm:text-2xl font-normal mt-1">Sizin İçin Listelendi</span>
            </h2>
          </div>

          {citiesLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-32 bg-gray-100 rounded-2xl" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(() => {
                // Öncelikli şehirler (sadece bu 4'ü göster)
                const priorityCitiesConfig = [
                  { name: 'İstanbul', slug: 'istanbul', gradient: 'from-pink-500 to-rose-500' },
                  { name: 'Ankara', slug: 'ankara', gradient: 'from-purple-500 to-violet-500' },
                  { name: 'Bursa', slug: 'bursa', gradient: 'from-rose-500 to-pink-500' },
                  { name: 'İzmir', slug: 'izmir', gradient: 'from-violet-500 to-purple-500' }
                ];
                
                // Her öncelikli şehir için database'den veriyi bul veya placeholder oluştur
                const displayCities = priorityCitiesConfig.map(config => {
                  const cityData = cities.find(city => city.name === config.name);
                  return cityData || {
                    name: config.name,
                    slug: config.slug,
                    venueCount: 0,
                    _id: config.slug
                  };
                });
                
                return displayCities.map((city, index) => (
                  <div 
                    key={city._id || city.name}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/${city.slug}-davet-evleri`)}
                  >
                    <div className={`relative bg-gradient-to-br ${priorityCitiesConfig[index].gradient} p-5 h-32 rounded-2xl flex flex-col justify-between overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl`}>
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-300"></div>
                      
                      <div className="relative z-10">
                        <MapPin className="w-6 h-6 text-white/90" />
                      </div>
                      
                      <div className="relative z-10">
                        <h3 className="text-white font-bold text-lg mb-0.5">
                          {city.name}
                        </h3>
                        <p className="text-white/80 text-xs font-medium">
                          Davet Evleri
                        </p>
                      </div>
                      
                      <ChevronRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section removed per user request */}
      {false && (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-pink-500 font-semibold text-sm uppercase tracking-wider">Nasıl Çalışır?</span>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-gray-900 mt-2 mb-4 tracking-tight">
              Organizasyonunu 3 Adımda Tamamla
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Hayalinizdeki etkinliği planlamak hiç bu kadar kolay olmamıştı
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full border-t-4 border-pink-500">
                {/* Step Number */}
                <div className="absolute -top-6 left-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                </div>
                
                {/* Icon */}
                <div className="mt-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-rose-100 rounded-2xl flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {/* Document frame - static */}
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      {/* Lines - animated */}
                      <path className="animate-write-line-1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6" />
                      <path className="animate-write-line-2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 16h6" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">
                  Formu Doldurun
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Etkinlik türünüzü, tarih ve kişi sayısını yazın. Sadece birkaç saniye sürer.
                </p>
              </div>
              
              {/* Arrow for desktop */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg className="w-8 h-8 text-pink-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full border-t-4 border-pink-500">
                {/* Step Number */}
                <div className="absolute -top-6 left-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                </div>
                
                {/* Icon */}
                <div className="mt-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-rose-100 rounded-2xl flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {/* Clipboard frame - static */}
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      {/* Checkmarks - animated */}
                      <circle className="animate-check-1" cx="9" cy="12" r="1.5" fill="currentColor" opacity="0" />
                      <circle className="animate-check-2" cx="9" cy="16" r="1.5" fill="currentColor" opacity="0" />
                      <path className="animate-check-line-1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12h3" opacity="0" />
                      <path className="animate-check-line-2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16h3" opacity="0" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">
                  Teklifleri Karşılaştırın
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Birden fazla davet evinden fiyat ve konsept teklifleri alın. Detayları inceleyin, bütçenize en uygun olanı seçin.
                </p>
              </div>
              
              {/* Arrow for desktop */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg className="w-8 h-8 text-pink-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full border-t-4 border-pink-500">
                {/* Step Number */}
                <div className="absolute -top-6 left-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                </div>
                
                {/* Icon */}
                <div className="mt-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-rose-100 rounded-2xl flex items-center justify-center mx-auto relative">
                    <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {/* Circle - static */}
                      <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                      {/* Check mark - animated */}
                      <path className="animate-draw-check" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    {/* Burst effect - appears when check completes */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="animate-burst-effect opacity-0">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-1 h-1 bg-pink-500 rounded-full"
                            style={{
                              transform: `rotate(${i * 45}deg) translateY(-12px)`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">
                  Organizasyonu Tamamlayın
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Seçtiğiniz davet eviyle iletişime geçin, sözleşmenizi oluşturun. Artık tek yapmanız gereken o özel günü yaşamak!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Recently Viewed Venues */}
      <RecentlyViewed />

      {/* Bulk Quote Form — temporarily disabled, keep for future re-enable */}
      {/* <BulkQuoteForm /> */}

      {/* Blog Section - Lazy Loaded */}
      <div ref={blogsSectionRef}>
      {blogsLoaded && blogs && blogs.length > 0 ? (
        <section className="py-20 bg-gradient-to-b from-white to-pink-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-pink-600 font-semibold text-sm uppercase tracking-wider">Blog</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mt-2 mb-4 tracking-tight">
                Organizasyon Rehberi
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Organizasyon sürecinizde size yardımcı olacak ipuçları ve bilgiler
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </div>

            {/* View All Blogs Button */}
            <div className="text-center mt-10">
              <Button 
                onClick={() => navigate('/blog')}
                size="lg"
                className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white px-8"
              >
                Tüm Blog Yazılarını Gör
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      ) : blogsLoaded ? null : (
        <section className="py-20 bg-gradient-to-b from-white to-pink-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-pink-600 font-semibold text-sm uppercase tracking-wider">Blog</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mt-2 mb-4 tracking-tight">Organizasyon Rehberi</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-40 bg-gray-200 rounded-2xl" />
                  <div className="mt-3 h-4 bg-gray-200 rounded w-3/4" />
                  <div className="mt-2 h-3 bg-gray-100 rounded w-full" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      </div>

      {/* Testimonials - Modern Design - Lazy Loaded */}
      <div ref={testimonialsSectionRef}>
      {testimonialsLoaded && testimonials.length > 0 ? (
      <section className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-100 rounded-full blur-3xl opacity-40"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-6xl font-semibold mb-4 bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700 bg-clip-text text-transparent tracking-tight">
              {settings?.testimonialsTitle || 'Mutlu Çiftlerimiz'}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {settings?.testimonialsSubtitle || 'Hayallerindeki düğünü gerçekleştiren çiftlerimizin hikayeleri'}
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id} 
                className={`group relative ${index === 1 ? 'md:translate-y-8' : ''}`}
              >
                {/* Card */}
                <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-pink-200 h-full">
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -left-2 w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-6 group-hover:rotate-0 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                    </svg>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-6 pt-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < testimonial.rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`} 
                      />
                    ))}
                  </div>

                  {/* Comment */}
                  <p className="text-gray-600 leading-relaxed mb-8 text-[15px]">
                    &ldquo;{testimonial.comment}&rdquo;
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        {testimonial.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {testimonial.venue}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          {testimonials.length > 0 && (
            <div className="text-center mt-16">
              <p className="text-gray-500 mb-4">
                <span className="font-semibold text-pink-600">{platformStats.total_whatsapp_clicks?.toLocaleString('tr-TR') || '0'}+</span> mutlu çiftin tercihi
              </p>
              <div className="flex items-center justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-2 text-gray-600 font-medium">4.9 / 5.0 Ortalama Puan</span>
              </div>
            </div>
          )}
        </div>
      </section>
      ) : null}
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => navigate('/hesabim')}
      />

      {/* Quick Quote Modal for Featured Venues */}
      <QuickQuoteModal 
        venue={quoteModalVenue}
        isOpen={!!quoteModalVenue}
        onClose={() => setQuoteModalVenue(null)}
      />

    </div>
  );
};

export default HomePage;
